import type { Block } from "./state"

export const DEFAULT_BLOCK_STYLE = "sawtooth_star"

// Exhaustive-switch guard. The compile-time win is the `never` parameter:
// any non-exhaustive `switch` on a closed union fails to type-check at the
// call site. The runtime throw is a belt-and-suspenders safety net for data
// that sneaks past the type system (JSON.parse, untyped tool args). Exported
// so all switches across modules use the same helper rather than reinventing
// it with a manual cast.
export function assertNever(x: never): never {
  throw new Error(`Unhandled variant: ${JSON.stringify(x)}`)
}

// Patch kinds dispatch the cut-size formula.
// `square` covers any plain square (center, corner, plain nine-patch cell).
// The two `flying_goose_*` kinds carry the no-waste 4-at-a-time formulas:
// the large background body square and the small sky/points square.
// `hst` is a square cut for the two-at-a-time half-square triangle method:
// pair of cut squares yields two HSTs of `size_units` finished edge.
// `qst` is a square cut for the four-at-a-time quarter-square triangle
// method: layer two squares, sew on both diagonals, cross-cut → 4 QSTs.
// `strip` is the first non-square patch: short edge = `size_units` (the strip
// width), long edge = `length_units` (cross-cut length). Both edges add the
// standard 2*seam allowance — the strip is sewn to neighbours on all four
// sides, like any pieced rectangle.
export type PatchKind = "square" | "flying_goose_large" | "flying_goose_small" | "hst" | "qst" | "strip"

// Assembly techniques the renderers know how to write up. Tagged on patches so
// `renderPlanMd` and `PageAssembly` can compose narrative without branching on
// face kinds. A hint is a knob in the assembly-template registry; a schema
// inherits whichever set its patches reference.
export type AssemblyHint =
  | "flying_geese_no_waste"
  | "hst_two_at_a_time"
  | "qst_four_at_a_time"
  | "strip_pieced"
  | "log_cabin_spiral"
  | "courthouse_steps"

// One piece type within a block. The schema lists every piece a single block
// produces; `count` is per-block, not per-quilt. Cut math reads this; render
// geometry lives on Face below.
export interface Patch {
  name: string         // piece name used as a key in Block.piece_roles ("center", "goose_large", ...)
  kind: PatchKind
  size_units: number   // edge length measured in grid units (short edge for strip kind)
  length_units?: number // long edge for non-square patches (strip kind); defaults to size_units
  count: number        // pieces of this kind per block
  default_role: string // fabric role that fills this piece by default
  label: string        // human-readable name for plans and diagrams
  assembly_hint?: AssemblyHint  // assembly technique that consumes this patch
}

// A face is one visible region on the block's painted surface. Its color and
// label are pulled from the patches it references via `roles`. For "square"
// and "strip" the only role key is "fill"; for "flying_goose" the keys are
// "body" and "sky"; for "hst" the keys are "a" and "b" (the two triangle
// halves); for "qst" the keys are "a" and "b" (each fills two opposite
// quarter-square triangles meeting at the face center).
// Orientation:
//   flying_goose — cardinal apex direction ("N" | "S" | "E" | "W").
//   hst — diagonal letters ("NW" | "NE" | "SE" | "SW") name the corner that
//   `a` occupies; `b` fills the opposite corner.
//   qst — "+" (a fills N+S triangles, b fills E+W) or "x" (a fills NE+SW
//   triangles along the TL→BR diagonal pair, b fills NW+SE).
//   strip — "H" (long axis horizontal, e.g. Rail Fence) or "V" (vertical).
//   Redundant with face.size in principle but signals intent and lets the
//   ASCII diagram emit a "strip H" / "strip V" label like flying_goose's
//   arrow notation.
export type FaceKind = "square" | "flying_goose" | "hst" | "qst" | "strip"

export type FaceOrientation =
  | "N" | "S" | "E" | "W"
  | "NW" | "NE" | "SE" | "SW"
  | "+" | "x"
  | "H" | "V"

export interface Face {
  pos: [number, number]            // top-left in grid units (col, row)
  size: [number, number]           // span in grid units (cols, rows)
  kind: FaceKind
  roles: Record<string, string>    // role-key → patch name
  orientation?: FaceOrientation
  rotation?: 0 | 1 | 2 | 3        // quarter-turns clockwise applied to content within bounding box
}

// Which way to press the seam allowance at a seam boundary.
// "open" means press flat (allowances away from each other).
// "to_a" / "to_b" mean both allowances fold toward the named face.
export type PressDirection = "open" | "to_a" | "to_b"

// One sewn seam between two assembled faces. Indices into schema.faces[].
// set_in is reserved for Y-seams (LeMoyne Star); no registered schema uses it yet.
// stage groups edges into numbered assembly phases (1, 2, …); omitting stage
// leaves the edge in the flat (unstaged) pool for backward compatibility.
export interface SeamEdge {
  a: number
  b: number
  press: PressDirection
  set_in?: boolean
  stage?: number
}

// A block schema is the spec for one block style.
// `grid_units` is (cols, rows); non-square blocks (e.g. a 3-row Rail Fence)
// drop in without another refactor.
export interface BlockSchema {
  name: string
  label: string
  grid_units: [number, number]
  patches: Patch[]
  faces: Face[]
  seam_graph: SeamEdge[]
}

const SAWTOOTH_STAR_SCHEMA: BlockSchema = {
  name: "sawtooth_star",
  label: "Sawtooth Star",
  grid_units: [4, 4],
  patches: [
    { name: "center",      kind: "square",             size_units: 2, count: 1, default_role: "center",     label: "center" },
    { name: "corner",      kind: "square",             size_units: 1, count: 4, default_role: "background", label: "corner" },
    { name: "goose_large", kind: "flying_goose_large", size_units: 2, count: 1, default_role: "background", label: "goose large sq", assembly_hint: "flying_geese_no_waste" },
    { name: "goose_small", kind: "flying_goose_small", size_units: 1, count: 4, default_role: "points",     label: "goose small sq", assembly_hint: "flying_geese_no_waste" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "square", roles: { fill: "corner" } },        // 0 TL-corner
    { pos: [3, 0], size: [1, 1], kind: "square", roles: { fill: "corner" } },        // 1 TR-corner
    { pos: [0, 3], size: [1, 1], kind: "square", roles: { fill: "corner" } },        // 2 BL-corner
    { pos: [3, 3], size: [1, 1], kind: "square", roles: { fill: "corner" } },        // 3 BR-corner
    { pos: [1, 1], size: [2, 2], kind: "square", roles: { fill: "center" } },        // 4 center
    { pos: [1, 0], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "S" }, // 5 top-goose
    { pos: [1, 3], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "N" }, // 6 bottom-goose
    { pos: [0, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "E" }, // 7 left-goose
    { pos: [3, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "W" }, // 8 right-goose
  ],
  // Press corners toward corner squares; geese toward center. Creates a
  // pinwheel of seam allowances around the center that nests at block joins.
  // Stage 1: attach corners to each goose unit.
  // Stage 2: join the four goose units to the center.
  seam_graph: [
    { a: 0, b: 5, press: "to_a", stage: 1 }, // TL-corner ↔ top-goose: V seam x=1
    { a: 1, b: 5, press: "to_a", stage: 1 }, // TR-corner ↔ top-goose: V seam x=3
    { a: 5, b: 4, press: "to_b", stage: 2 }, // top-goose ↔ center: H seam y=1
    { a: 0, b: 7, press: "to_a", stage: 1 }, // TL-corner ↔ left-goose: H seam y=1
    { a: 2, b: 7, press: "to_a", stage: 1 }, // BL-corner ↔ left-goose: H seam y=3
    { a: 7, b: 4, press: "to_b", stage: 2 }, // left-goose ↔ center: V seam x=1
    { a: 1, b: 8, press: "to_a", stage: 1 }, // TR-corner ↔ right-goose: H seam y=1
    { a: 3, b: 8, press: "to_a", stage: 1 }, // BR-corner ↔ right-goose: H seam y=3
    { a: 8, b: 4, press: "to_b", stage: 2 }, // right-goose ↔ center: V seam x=3
    { a: 2, b: 6, press: "to_a", stage: 1 }, // BL-corner ↔ bottom-goose: V seam x=1
    { a: 3, b: 6, press: "to_a", stage: 1 }, // BR-corner ↔ bottom-goose: V seam x=3
    { a: 6, b: 4, press: "to_b", stage: 2 }, // bottom-goose ↔ center: H seam y=3
  ],
}

// Three-fabric Nine-Patch: 3×3 grid of unit squares, no flying geese, no
// orientation. Corner/edge/center each take a different fabric role so the
// same three-fabric stash that drives Sawtooth Star also drives Nine-Patch.
const NINE_PATCH_SCHEMA: BlockSchema = {
  name: "nine_patch",
  label: "Nine-Patch",
  grid_units: [3, 3],
  patches: [
    { name: "center", kind: "square", size_units: 1, count: 1, default_role: "center",     label: "center" },
    { name: "corner", kind: "square", size_units: 1, count: 4, default_role: "background", label: "corner" },
    { name: "edge",   kind: "square", size_units: 1, count: 4, default_role: "points",     label: "edge" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "square", roles: { fill: "corner" } }, // 0 TL-corner
    { pos: [2, 0], size: [1, 1], kind: "square", roles: { fill: "corner" } }, // 1 TR-corner
    { pos: [0, 2], size: [1, 1], kind: "square", roles: { fill: "corner" } }, // 2 BL-corner
    { pos: [2, 2], size: [1, 1], kind: "square", roles: { fill: "corner" } }, // 3 BR-corner
    { pos: [1, 0], size: [1, 1], kind: "square", roles: { fill: "edge" } },   // 4 top-edge
    { pos: [0, 1], size: [1, 1], kind: "square", roles: { fill: "edge" } },   // 5 left-edge
    { pos: [2, 1], size: [1, 1], kind: "square", roles: { fill: "edge" } },   // 6 right-edge
    { pos: [1, 2], size: [1, 1], kind: "square", roles: { fill: "edge" } },   // 7 bottom-edge
    { pos: [1, 1], size: [1, 1], kind: "square", roles: { fill: "center" } }, // 8 center
  ],
  // Alternating-row press: row 0 right (to_b), row 1 left (to_a), row 2 right
  // (to_b). Row-joining seams press open so they nest at intersections.
  // Stage 1: sew pieces within each of the three rows.
  // Stage 2: join the three completed rows.
  seam_graph: [
    { a: 0, b: 4, press: "to_b", stage: 1 }, // row 0: TL-corner ↔ top-edge (press right)
    { a: 4, b: 1, press: "to_b", stage: 1 }, // row 0: top-edge ↔ TR-corner (press right)
    { a: 5, b: 8, press: "to_a", stage: 1 }, // row 1: left-edge ↔ center (press left)
    { a: 8, b: 6, press: "to_a", stage: 1 }, // row 1: center ↔ right-edge (press left)
    { a: 2, b: 7, press: "to_b", stage: 1 }, // row 2: BL-corner ↔ bottom-edge (press right)
    { a: 7, b: 3, press: "to_b", stage: 1 }, // row 2: bottom-edge ↔ BR-corner (press right)
    { a: 0, b: 5, press: "open", stage: 2 }, // row join col 0
    { a: 4, b: 8, press: "open", stage: 2 }, // row join col 1
    { a: 1, b: 6, press: "open", stage: 2 }, // row join col 2
    { a: 5, b: 2, press: "open", stage: 2 }, // row join col 0
    { a: 8, b: 7, press: "open", stage: 2 }, // row join col 1
    { a: 6, b: 3, press: "open", stage: 2 }, // row join col 2
  ],
}

// Four-HST Pinwheel: a 2×2 grid where each cell is a half-square triangle.
// The four HSTs share quarter-turn symmetry, so the same two fabrics fill
// every cell — `vane` (the spinning blade) on one diagonal half, `field`
// (the negative space) on the other. Diagonal orientation rotates around
// the block center to make the pinwheel spin: NE / SE / SW / NW.
const PINWHEEL_SCHEMA: BlockSchema = {
  name: "pinwheel",
  label: "Pinwheel",
  grid_units: [2, 2],
  // Two-at-a-time HSTs: pair one vane square with one field square, sew on
  // the diagonal, cross-cut → 2 HSTs. A 4-HST Pinwheel needs 2 cut squares
  // of each fabric per block. `count` is cut squares per block, matching
  // the no-waste flying-geese precedent (1 large + 4 small → 4 geese).
  patches: [
    { name: "vane",  kind: "hst", size_units: 1, count: 2, default_role: "points",     label: "vane HST",  assembly_hint: "hst_two_at_a_time" },
    { name: "field", kind: "hst", size_units: 1, count: 2, default_role: "background", label: "field HST", assembly_hint: "hst_two_at_a_time" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "hst", roles: { a: "vane", b: "field" }, orientation: "NE" }, // 0 TL
    { pos: [1, 0], size: [1, 1], kind: "hst", roles: { a: "vane", b: "field" }, orientation: "SE" }, // 1 TR
    { pos: [1, 1], size: [1, 1], kind: "hst", roles: { a: "vane", b: "field" }, orientation: "SW" }, // 2 BR
    { pos: [0, 1], size: [1, 1], kind: "hst", roles: { a: "vane", b: "field" }, orientation: "NW" }, // 3 BL
  ],
  // Clockwise swirl: press each seam toward the next block in the rotation
  // so seam allowances spiral around the center and reduce bulk there.
  seam_graph: [
    { a: 0, b: 1, press: "to_b" }, // TL ↔ TR: V seam x=1, press right
    { a: 1, b: 2, press: "to_b" }, // TR ↔ BR: H seam y=1, press down
    { a: 3, b: 2, press: "to_a" }, // BL ↔ BR: V seam x=1, press left
    { a: 0, b: 3, press: "to_a" }, // TL ↔ BL: H seam y=1, press up
  ],
}

// Ohio Star: same 4×4 skeleton as Sawtooth Star — 4 corner squares + 4 flying
// geese — but the 2×2 center swaps from a plain square to a quarter-square
// triangle. The QST splits the center into four triangles meeting at the
// middle, with two opposite triangles in the focal "center" fabric and the
// other two in the "points" fabric (matching the goose tips). Adds an
// assembly_hint pair that the renderer composes alongside the existing
// flying-geese hint without a third hand-tuned `if` block.
const OHIO_STAR_SCHEMA: BlockSchema = {
  name: "ohio_star",
  label: "Ohio Star",
  grid_units: [4, 4],
  patches: [
    { name: "corner",      kind: "square",             size_units: 1, count: 4, default_role: "background", label: "corner" },
    { name: "goose_large", kind: "flying_goose_large", size_units: 2, count: 1, default_role: "background", label: "goose large sq", assembly_hint: "flying_geese_no_waste" },
    { name: "goose_small", kind: "flying_goose_small", size_units: 1, count: 4, default_role: "points",     label: "goose small sq", assembly_hint: "flying_geese_no_waste" },
    // Four-at-a-time QST: layer one `qst_a` square with one `qst_b` square,
    // sew on both diagonals, cross-cut → 4 identical QSTs. Ohio Star uses 1
    // QST in the center, so 1 cut square per fabric per block.
    { name: "qst_a", kind: "qst", size_units: 2, count: 1, default_role: "center", label: "QST a (N+S)", assembly_hint: "qst_four_at_a_time" },
    { name: "qst_b", kind: "qst", size_units: 2, count: 1, default_role: "points", label: "QST b (E+W)", assembly_hint: "qst_four_at_a_time" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "square",       roles: { fill: "corner" },                                            }, // 0 TL-corner
    { pos: [3, 0], size: [1, 1], kind: "square",       roles: { fill: "corner" },                                            }, // 1 TR-corner
    { pos: [0, 3], size: [1, 1], kind: "square",       roles: { fill: "corner" },                                            }, // 2 BL-corner
    { pos: [3, 3], size: [1, 1], kind: "square",       roles: { fill: "corner" },                                            }, // 3 BR-corner
    { pos: [1, 1], size: [2, 2], kind: "qst",          roles: { a: "qst_a", b: "qst_b" },          orientation: "+"          }, // 4 center-QST
    { pos: [1, 0], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "S"  }, // 5 top-goose
    { pos: [1, 3], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "N"  }, // 6 bottom-goose
    { pos: [0, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "E"  }, // 7 left-goose
    { pos: [3, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "W"  }, // 8 right-goose
  ],
  // Same 12-edge press plan as Sawtooth Star: corners toward corners, geese toward center QST.
  seam_graph: [
    { a: 0, b: 5, press: "to_a" }, // TL-corner ↔ top-goose: V seam x=1
    { a: 1, b: 5, press: "to_a" }, // TR-corner ↔ top-goose: V seam x=3
    { a: 5, b: 4, press: "to_b" }, // top-goose ↔ center: H seam y=1
    { a: 0, b: 7, press: "to_a" }, // TL-corner ↔ left-goose: H seam y=1
    { a: 2, b: 7, press: "to_a" }, // BL-corner ↔ left-goose: H seam y=3
    { a: 7, b: 4, press: "to_b" }, // left-goose ↔ center: V seam x=1
    { a: 1, b: 8, press: "to_a" }, // TR-corner ↔ right-goose: H seam y=1
    { a: 3, b: 8, press: "to_a" }, // BR-corner ↔ right-goose: H seam y=3
    { a: 8, b: 4, press: "to_b" }, // right-goose ↔ center: V seam x=3
    { a: 2, b: 6, press: "to_a" }, // BL-corner ↔ bottom-goose: V seam x=1
    { a: 3, b: 6, press: "to_a" }, // BR-corner ↔ bottom-goose: V seam x=3
    { a: 6, b: 4, press: "to_b" }, // bottom-goose ↔ center: H seam y=3
  ],
}

// Three-rail Rail Fence: a 3×2 grid of three vertical strips standing
// shoulder to shoulder. First registered non-square block — finished block
// is 3u × 2u (e.g. 6"×4" at the default 2" unit), wider than tall, so
// horizontal sashing strips between rows must be cut shorter than vertical
// sashing strips between columns. Three strips per block, one fabric per
// strip; the typical rail-fence colorway runs dark / medium / light. Blocks
// rotate 90° between rows in a layout — that "fence" zig-zag is a
// layout-level concern, not part of the schema yet.
const RAIL_FENCE_SCHEMA: BlockSchema = {
  name: "rail_fence",
  label: "Rail Fence",
  grid_units: [3, 2],
  // Three strips, one each. `size_units = 1` is the short edge (strip width
  // in grid units); `length_units = 2` is the long edge. Strip-pieced
  // construction sews three WOF strips lengthwise then cross-cuts at
  // length_units * unit_inches + 2*seam — the assembly hint composes those
  // steps without renderer branches.
  patches: [
    { name: "rail_a", kind: "strip", size_units: 1, length_units: 2, count: 1, default_role: "background", label: "rail A", assembly_hint: "strip_pieced" },
    { name: "rail_b", kind: "strip", size_units: 1, length_units: 2, count: 1, default_role: "points",     label: "rail B", assembly_hint: "strip_pieced" },
    { name: "rail_c", kind: "strip", size_units: 1, length_units: 2, count: 1, default_role: "center",     label: "rail C", assembly_hint: "strip_pieced" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 2], kind: "strip", roles: { fill: "rail_a" }, orientation: "V" }, // 0
    { pos: [1, 0], size: [1, 2], kind: "strip", roles: { fill: "rail_b" }, orientation: "V" }, // 1
    { pos: [2, 0], size: [1, 2], kind: "strip", roles: { fill: "rail_c" }, orientation: "V" }, // 2
  ],
  seam_graph: [
    { a: 0, b: 1, press: "to_b" }, // rail_a ↔ rail_b: V seam x=1
    { a: 1, b: 2, press: "to_b" }, // rail_b ↔ rail_c: V seam x=2
  ],
}

// Plain Block: a single solid square that fills the block's entire footprint.
// Used as the alternating block in layouts like Single Irish Chain where every
// other cell is a plain background square. `unit_inches` should be set to the
// primary block's finished width so the plain block occupies the same cell.
const PLAIN_BLOCK_SCHEMA: BlockSchema = {
  name: "plain_block",
  label: "Plain Block",
  grid_units: [1, 1],
  patches: [
    { name: "field", kind: "square", size_units: 1, count: 1, default_role: "background", label: "field" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "square", roles: { fill: "field" } },
  ],
  seam_graph: [],
}

// Five-Patch: 5×5 grid of unit plain squares. "Chain" squares fill the 16
// non-cross positions (col≠2 AND row≠2); "field" squares (background) fill
// the 9 cross positions (center column + center row).
const FIVE_PATCH_SCHEMA: BlockSchema = {
  name: "five_patch",
  label: "Five-Patch",
  grid_units: [5, 5],
  patches: [
    { name: "chain", kind: "square", size_units: 1, count: 16, default_role: "chain",      label: "chain square" },
    { name: "field", kind: "square", size_units: 1, count: 9,  default_role: "background", label: "field square" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [1, 0], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [2, 0], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [3, 0], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [4, 0], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [0, 1], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [1, 1], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [2, 1], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [3, 1], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [4, 1], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [0, 2], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [1, 2], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [2, 2], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [3, 2], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [4, 2], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [0, 3], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [1, 3], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [2, 3], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [3, 3], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [4, 3], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [0, 4], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [1, 4], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [2, 4], size: [1, 1], kind: "square", roles: { fill: "field" } },
    { pos: [3, 4], size: [1, 1], kind: "square", roles: { fill: "chain" } },
    { pos: [4, 4], size: [1, 1], kind: "square", roles: { fill: "chain" } },
  ],
  seam_graph: [],
}

// Log Cabin: a 7×7 grid of concentric strip rings around a center square.
// Three rings of strips (each 1u wide) spiral around a 1u×1u center, adding
// one strip to the growing block on each of four sides per ring. Traditional
// light/dark split: light strips occupy the TOP and LEFT of every ring (the
// block's top-left quadrant), dark strips occupy the RIGHT and BOTTOM (the
// block's bottom-right quadrant). This \-diagonal orientation is the classic
// "night and day" split — light corner at top-left drives Barn Raising's
// concentric dark-center diamond rings. At unit=2": strips 2" finished
// (cut 2½"), finished block 14"×14". Seam graph records the clockwise spiral
// assembly order (top→right→bottom→left) so the chain-piecing section lists
// each seam in sewing rotation order.
const LOG_CABIN_SCHEMA: BlockSchema = {
  name: "log_cabin",
  label: "Log Cabin",
  grid_units: [7, 7],
  patches: [
    { name: "center", kind: "square", size_units: 1, count: 1, default_role: "center", label: "center" },
    // Ring 1 — clockwise from top: top (light), right (dark), bottom (dark), left (light)
    { name: "r1a", kind: "strip", size_units: 1, length_units: 1, count: 1, default_role: "light", label: "L1 top",    assembly_hint: "log_cabin_spiral" },
    { name: "r1b", kind: "strip", size_units: 1, length_units: 2, count: 1, default_role: "dark",  label: "D1 right",  assembly_hint: "log_cabin_spiral" },
    { name: "r1c", kind: "strip", size_units: 1, length_units: 2, count: 1, default_role: "dark",  label: "D1 bottom", assembly_hint: "log_cabin_spiral" },
    { name: "r1d", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "light", label: "L1 left",   assembly_hint: "log_cabin_spiral" },
    // Ring 2 — same clockwise order
    { name: "r2a", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "light", label: "L2 top",    assembly_hint: "log_cabin_spiral" },
    { name: "r2b", kind: "strip", size_units: 1, length_units: 4, count: 1, default_role: "dark",  label: "D2 right",  assembly_hint: "log_cabin_spiral" },
    { name: "r2c", kind: "strip", size_units: 1, length_units: 4, count: 1, default_role: "dark",  label: "D2 bottom", assembly_hint: "log_cabin_spiral" },
    { name: "r2d", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "light", label: "L2 left",   assembly_hint: "log_cabin_spiral" },
    // Ring 3 — same clockwise order
    { name: "r3a", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "light", label: "L3 top",    assembly_hint: "log_cabin_spiral" },
    { name: "r3b", kind: "strip", size_units: 1, length_units: 6, count: 1, default_role: "dark",  label: "D3 right",  assembly_hint: "log_cabin_spiral" },
    { name: "r3c", kind: "strip", size_units: 1, length_units: 6, count: 1, default_role: "dark",  label: "D3 bottom", assembly_hint: "log_cabin_spiral" },
    { name: "r3d", kind: "strip", size_units: 1, length_units: 7, count: 1, default_role: "light", label: "L3 left",   assembly_hint: "log_cabin_spiral" },
  ],
  faces: [
    { pos: [3, 3], size: [1, 1], kind: "square", roles: { fill: "center" } },              // 0 center
    { pos: [3, 2], size: [1, 1], kind: "strip",  roles: { fill: "r1a" }, orientation: "V" }, // 1 ring1 light short (above center)
    { pos: [4, 2], size: [1, 2], kind: "strip",  roles: { fill: "r1b" }, orientation: "V" }, // 2 ring1 dark long   (right)
    { pos: [3, 4], size: [2, 1], kind: "strip",  roles: { fill: "r1c" }, orientation: "H" }, // 3 ring1 dark short  (below)
    { pos: [2, 2], size: [1, 3], kind: "strip",  roles: { fill: "r1d" }, orientation: "V" }, // 4 ring1 light long  (left)
    { pos: [2, 1], size: [3, 1], kind: "strip",  roles: { fill: "r2a" }, orientation: "H" }, // 5 ring2 light short (above ring1)
    { pos: [5, 1], size: [1, 4], kind: "strip",  roles: { fill: "r2b" }, orientation: "V" }, // 6 ring2 dark long   (right)
    { pos: [2, 5], size: [4, 1], kind: "strip",  roles: { fill: "r2c" }, orientation: "H" }, // 7 ring2 dark short  (below)
    { pos: [1, 1], size: [1, 5], kind: "strip",  roles: { fill: "r2d" }, orientation: "V" }, // 8 ring2 light long  (left)
    { pos: [1, 0], size: [5, 1], kind: "strip",  roles: { fill: "r3a" }, orientation: "H" }, // 9 ring3 light short (above ring2)
    { pos: [6, 0], size: [1, 6], kind: "strip",  roles: { fill: "r3b" }, orientation: "V" }, // 10 ring3 dark long  (right)
    { pos: [1, 6], size: [6, 1], kind: "strip",  roles: { fill: "r3c" }, orientation: "H" }, // 11 ring3 dark short (below)
    { pos: [0, 0], size: [1, 7], kind: "strip",  roles: { fill: "r3d" }, orientation: "V" }, // 12 ring3 light long (left)
  ],
  // Spiral seam order: each edge records the boundary between adjacent strips
  // in the assembly sequence (center → r1a → r1b → r1c → r1d → r2a → …).
  // All seams press outward — toward the newly added outer strip (to_b).
  seam_graph: [
    { a: 0,  b: 1,  press: "to_b" }, // center ↔ r1a
    { a: 1,  b: 2,  press: "to_b" }, // r1a ↔ r1b
    { a: 2,  b: 3,  press: "to_b" }, // r1b ↔ r1c
    { a: 3,  b: 4,  press: "to_b" }, // r1c ↔ r1d
    { a: 4,  b: 5,  press: "to_b" }, // r1d ↔ r2a
    { a: 5,  b: 6,  press: "to_b" }, // r2a ↔ r2b
    { a: 6,  b: 7,  press: "to_b" }, // r2b ↔ r2c
    { a: 7,  b: 8,  press: "to_b" }, // r2c ↔ r2d
    { a: 8,  b: 9,  press: "to_b" }, // r2d ↔ r3a
    { a: 9,  b: 10, press: "to_b" }, // r3a ↔ r3b
    { a: 10, b: 11, press: "to_b" }, // r3b ↔ r3c
    { a: 11, b: 12, press: "to_b" }, // r3c ↔ r3d
  ],
}

// Courthouse Steps is the symmetric sibling of Log Cabin. Instead of a
// clockwise spiral, strips are added in parallel pairs: horizontal pair
// (top + bottom) in light, then vertical pair (left + right) in dark, one
// ring at a time. The result is a cross of horizontal and vertical bands —
// when multiple blocks are set together the bands align across the quilt,
// producing a graphic grid or chain effect. Same 7×7 grid as Log Cabin; all
// cut infrastructure carries through without change. At unit=2": strips 2½"
// wide; horizontal pairs cut 2½" and 6½"; vertical pairs cut 6½", 10½", 14½".
const COURTHOUSE_STEPS_SCHEMA: BlockSchema = {
  name: "courthouse_steps",
  label: "Courthouse Steps",
  grid_units: [7, 7],
  patches: [
    { name: "center",   kind: "square", size_units: 1,                  count: 1, default_role: "center", label: "center" },
    // Ring 1 — horizontal pair (top + bottom) light, then vertical pair (left + right) dark
    { name: "r1h_top", kind: "strip", size_units: 1, length_units: 1, count: 1, default_role: "light", label: "L1 top",    assembly_hint: "courthouse_steps" },
    { name: "r1h_bot", kind: "strip", size_units: 1, length_units: 1, count: 1, default_role: "light", label: "L1 bottom", assembly_hint: "courthouse_steps" },
    { name: "r1v_lft", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "dark",  label: "D1 left",   assembly_hint: "courthouse_steps" },
    { name: "r1v_rgt", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "dark",  label: "D1 right",  assembly_hint: "courthouse_steps" },
    // Ring 2
    { name: "r2h_top", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "light", label: "L2 top",    assembly_hint: "courthouse_steps" },
    { name: "r2h_bot", kind: "strip", size_units: 1, length_units: 3, count: 1, default_role: "light", label: "L2 bottom", assembly_hint: "courthouse_steps" },
    { name: "r2v_lft", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "dark",  label: "D2 left",   assembly_hint: "courthouse_steps" },
    { name: "r2v_rgt", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "dark",  label: "D2 right",  assembly_hint: "courthouse_steps" },
    // Ring 3
    { name: "r3h_top", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "light", label: "L3 top",    assembly_hint: "courthouse_steps" },
    { name: "r3h_bot", kind: "strip", size_units: 1, length_units: 5, count: 1, default_role: "light", label: "L3 bottom", assembly_hint: "courthouse_steps" },
    { name: "r3v_lft", kind: "strip", size_units: 1, length_units: 7, count: 1, default_role: "dark",  label: "D3 left",   assembly_hint: "courthouse_steps" },
    { name: "r3v_rgt", kind: "strip", size_units: 1, length_units: 7, count: 1, default_role: "dark",  label: "D3 right",  assembly_hint: "courthouse_steps" },
  ],
  faces: [
    { pos: [3, 3], size: [1, 1], kind: "square", roles: { fill: "center"   } },
    // Ring 1
    { pos: [3, 2], size: [1, 1], kind: "strip", roles: { fill: "r1h_top" }, orientation: "H" },
    { pos: [3, 4], size: [1, 1], kind: "strip", roles: { fill: "r1h_bot" }, orientation: "H" },
    { pos: [2, 2], size: [1, 3], kind: "strip", roles: { fill: "r1v_lft" }, orientation: "V" },
    { pos: [4, 2], size: [1, 3], kind: "strip", roles: { fill: "r1v_rgt" }, orientation: "V" },
    // Ring 2
    { pos: [2, 1], size: [3, 1], kind: "strip", roles: { fill: "r2h_top" }, orientation: "H" },
    { pos: [2, 5], size: [3, 1], kind: "strip", roles: { fill: "r2h_bot" }, orientation: "H" },
    { pos: [1, 1], size: [1, 5], kind: "strip", roles: { fill: "r2v_lft" }, orientation: "V" },
    { pos: [5, 1], size: [1, 5], kind: "strip", roles: { fill: "r2v_rgt" }, orientation: "V" },
    // Ring 3
    { pos: [1, 0], size: [5, 1], kind: "strip", roles: { fill: "r3h_top" }, orientation: "H" },
    { pos: [1, 6], size: [5, 1], kind: "strip", roles: { fill: "r3h_bot" }, orientation: "H" },
    { pos: [0, 0], size: [1, 7], kind: "strip", roles: { fill: "r3v_lft" }, orientation: "V" },
    { pos: [6, 0], size: [1, 7], kind: "strip", roles: { fill: "r3v_rgt" }, orientation: "V" },
  ],
  // Assembly order: sew horizontal pair (top+bottom), then vertical pair (left+right), per ring.
  // Each seam presses outward (to_b). Edge indices reference faces[] above.
  seam_graph: [
    { a: 0,  b: 1,  press: "to_b" }, // center ↔ r1h_top
    { a: 0,  b: 2,  press: "to_b" }, // center ↔ r1h_bot
    { a: 1,  b: 3,  press: "to_b" }, // column → r1v_lft
    { a: 1,  b: 4,  press: "to_b" }, // column → r1v_rgt
    { a: 3,  b: 5,  press: "to_b" }, // 3×3 → r2h_top
    { a: 3,  b: 6,  press: "to_b" }, // 3×3 → r2h_bot
    { a: 5,  b: 7,  press: "to_b" }, // 3×5 → r2v_lft
    { a: 5,  b: 8,  press: "to_b" }, // 3×5 → r2v_rgt
    { a: 7,  b: 9,  press: "to_b" }, // 5×5 → r3h_top
    { a: 7,  b: 10, press: "to_b" }, // 5×5 → r3h_bot
    { a: 9,  b: 11, press: "to_b" }, // 5×7 → r3v_lft
    { a: 9,  b: 12, press: "to_b" }, // 5×7 → r3v_rgt
  ],
}

// Churn Dash: a 3×3 block — same grid as Nine-Patch — where the four corners
// are half-square triangles instead of plain squares. The HST corners create
// diagonal cuts that echo the handle tips of a butter-churn dash tool. Four
// solid sash squares form the cross arms; a contrasting center anchors the
// middle. First schema to combine HST and square face kinds in one block.
// Three-fabric palette (background / points / center) matches Nine-Patch so
// the blocks can alternate with a plain block for a simple chain layout.
// Construction: cut 4 HST pairs (2 bg_hst cut squares + 2 handle_hst cut squares)
// using the two-at-a-time method, cut 4 sash squares and 1 center square, then
// assemble in three rows pressing row 0 and 2 right, row 1 left, row joins open.
const CHURN_DASH_SCHEMA: BlockSchema = {
  name: "churn_dash",
  label: "Churn Dash",
  grid_units: [3, 3],
  patches: [
    { name: "bg_hst",     kind: "hst",    size_units: 1, count: 2, default_role: "background", label: "background HST", assembly_hint: "hst_two_at_a_time" },
    { name: "handle_hst", kind: "hst",    size_units: 1, count: 2, default_role: "points",     label: "handle HST",     assembly_hint: "hst_two_at_a_time" },
    { name: "sash",       kind: "square", size_units: 1, count: 4, default_role: "points",     label: "sash" },
    { name: "center",     kind: "square", size_units: 1, count: 1, default_role: "center",     label: "center" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "hst",    roles: { a: "handle_hst", b: "bg_hst" }, orientation: "NW" }, // 0 TL-HST
    { pos: [2, 0], size: [1, 1], kind: "hst",    roles: { a: "handle_hst", b: "bg_hst" }, orientation: "NE" }, // 1 TR-HST
    { pos: [0, 2], size: [1, 1], kind: "hst",    roles: { a: "handle_hst", b: "bg_hst" }, orientation: "SW" }, // 2 BL-HST
    { pos: [2, 2], size: [1, 1], kind: "hst",    roles: { a: "handle_hst", b: "bg_hst" }, orientation: "SE" }, // 3 BR-HST
    { pos: [1, 0], size: [1, 1], kind: "square", roles: { fill: "sash" } },                                    // 4 top-sash
    { pos: [0, 1], size: [1, 1], kind: "square", roles: { fill: "sash" } },                                    // 5 left-sash
    { pos: [2, 1], size: [1, 1], kind: "square", roles: { fill: "sash" } },                                    // 6 right-sash
    { pos: [1, 2], size: [1, 1], kind: "square", roles: { fill: "sash" } },                                    // 7 bottom-sash
    { pos: [1, 1], size: [1, 1], kind: "square", roles: { fill: "center" } },                                  // 8 center
  ],
  // Sew in 3 rows; alternate press direction (rows 0+2 right, row 1 left) so
  // seam allowances nest at intersections. Row-join seams press open.
  seam_graph: [
    { a: 0, b: 4, press: "to_b" }, // row 0: TL-HST ↔ top-sash (press right)
    { a: 4, b: 1, press: "to_b" }, // row 0: top-sash ↔ TR-HST (press right)
    { a: 5, b: 8, press: "to_a" }, // row 1: left-sash ↔ center (press left)
    { a: 8, b: 6, press: "to_a" }, // row 1: center ↔ right-sash (press left)
    { a: 2, b: 7, press: "to_b" }, // row 2: BL-HST ↔ bottom-sash (press right)
    { a: 7, b: 3, press: "to_b" }, // row 2: bottom-sash ↔ BR-HST (press right)
    { a: 0, b: 5, press: "open" }, // row join col 0
    { a: 4, b: 8, press: "open" }, // row join col 1
    { a: 1, b: 6, press: "open" }, // row join col 2
    { a: 5, b: 2, press: "open" }, // row join col 0
    { a: 8, b: 7, press: "open" }, // row join col 1
    { a: 6, b: 3, press: "open" }, // row join col 2
  ],
}

// Shoofly: a 3×3 block — same grid as Nine-Patch and Churn Dash — where HST
// corners carry the accent color outward while plain background squares fill
// the four edge positions. The open cross of background squares lets the four
// accent triangles "fly" outward at each corner. Differs from Churn Dash only
// in the edge squares: Shoofly edges are background (open cross), Churn Dash
// edges are points (solid cross arms). Three-fabric palette (background /
// points / center) matches Nine-Patch and Churn Dash so these blocks alternate
// cleanly with plain_block. Construction: 2 bg_hst + 2 point_hst cut squares
// (two-at-a-time) → 4 HSTs, plus 4 plain edge squares and 1 center square.
// Assemble in 3 rows pressing alternately right/left; row joins press open.
const SHOOFLY_SCHEMA: BlockSchema = {
  name: "shoofly",
  label: "Shoofly",
  grid_units: [3, 3],
  patches: [
    { name: "bg_hst",    kind: "hst",    size_units: 1, count: 2, default_role: "background", label: "background HST", assembly_hint: "hst_two_at_a_time" },
    { name: "point_hst", kind: "hst",    size_units: 1, count: 2, default_role: "points",     label: "points HST",     assembly_hint: "hst_two_at_a_time" },
    { name: "edge",      kind: "square", size_units: 1, count: 4, default_role: "background", label: "edge" },
    { name: "center",    kind: "square", size_units: 1, count: 1, default_role: "center",     label: "center" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "hst",    roles: { a: "point_hst", b: "bg_hst" }, orientation: "NW" }, // 0 TL-HST
    { pos: [2, 0], size: [1, 1], kind: "hst",    roles: { a: "point_hst", b: "bg_hst" }, orientation: "NE" }, // 1 TR-HST
    { pos: [0, 2], size: [1, 1], kind: "hst",    roles: { a: "point_hst", b: "bg_hst" }, orientation: "SW" }, // 2 BL-HST
    { pos: [2, 2], size: [1, 1], kind: "hst",    roles: { a: "point_hst", b: "bg_hst" }, orientation: "SE" }, // 3 BR-HST
    { pos: [1, 0], size: [1, 1], kind: "square", roles: { fill: "edge" } },                                   // 4 top-edge
    { pos: [0, 1], size: [1, 1], kind: "square", roles: { fill: "edge" } },                                   // 5 left-edge
    { pos: [2, 1], size: [1, 1], kind: "square", roles: { fill: "edge" } },                                   // 6 right-edge
    { pos: [1, 2], size: [1, 1], kind: "square", roles: { fill: "edge" } },                                   // 7 bottom-edge
    { pos: [1, 1], size: [1, 1], kind: "square", roles: { fill: "center" } },                                 // 8 center
  ],
  // Sew in 3 rows; alternate press direction (rows 0+2 right, row 1 left) so
  // seam allowances nest at intersections. Row-join seams press open.
  seam_graph: [
    { a: 0, b: 4, press: "to_b" }, // row 0: TL-HST ↔ top-edge (press right)
    { a: 4, b: 1, press: "to_b" }, // row 0: top-edge ↔ TR-HST (press right)
    { a: 5, b: 8, press: "to_a" }, // row 1: left-edge ↔ center (press left)
    { a: 8, b: 6, press: "to_a" }, // row 1: center ↔ right-edge (press left)
    { a: 2, b: 7, press: "to_b" }, // row 2: BL-HST ↔ bottom-edge (press right)
    { a: 7, b: 3, press: "to_b" }, // row 2: bottom-edge ↔ BR-HST (press right)
    { a: 0, b: 5, press: "open" }, // row join col 0
    { a: 4, b: 8, press: "open" }, // row join col 1
    { a: 1, b: 6, press: "open" }, // row join col 2
    { a: 5, b: 2, press: "open" }, // row join col 0
    { a: 8, b: 7, press: "open" }, // row join col 1
    { a: 6, b: 3, press: "open" }, // row join col 2
  ],
}

// Friendship Star: a 3×3 block in the nine-patch family where four HSTs sit at
// the EDGE positions (top / right / bottom / left) and plain background squares
// sit at the four CORNERS. The HSTs are oriented clockwise (top=NE, right=SE,
// bottom=SW, left=NW) so the star arms spin like a compass rose. Visually
// distinct from Churn Dash and Shoofly — those put HSTs at the corners; this
// puts them at the edges — while sharing the same three-fabric palette
// (background / points / center) and the same two-at-a-time HST construction.
// Alternates cleanly with plain_block like any other nine-patch sibling.
const FRIENDSHIP_STAR_SCHEMA: BlockSchema = {
  name: "friendship_star",
  label: "Friendship Star",
  grid_units: [3, 3],
  patches: [
    { name: "bg_hst",   kind: "hst",    size_units: 1, count: 2, default_role: "background", label: "background HST", assembly_hint: "hst_two_at_a_time" },
    { name: "star_hst", kind: "hst",    size_units: 1, count: 2, default_role: "points",     label: "star HST",       assembly_hint: "hst_two_at_a_time" },
    { name: "corner",   kind: "square", size_units: 1, count: 4, default_role: "background", label: "corner" },
    { name: "center",   kind: "square", size_units: 1, count: 1, default_role: "center",     label: "center" },
  ],
  faces: [
    // 4 plain corner squares (background) — corners are background, edges carry the star arms
    { pos: [0, 0], size: [1, 1], kind: "square", roles: { fill: "corner" }                                 }, // 0 TL
    { pos: [2, 0], size: [1, 1], kind: "square", roles: { fill: "corner" }                                 }, // 1 TR
    { pos: [0, 2], size: [1, 1], kind: "square", roles: { fill: "corner" }                                 }, // 2 BL
    { pos: [2, 2], size: [1, 1], kind: "square", roles: { fill: "corner" }                                 }, // 3 BR
    // 4 edge HSTs — star arms, clockwise spin: top=NE, right=SE, bottom=SW, left=NW
    { pos: [1, 0], size: [1, 1], kind: "hst",    roles: { a: "star_hst", b: "bg_hst" }, orientation: "NE" }, // 4 top
    { pos: [2, 1], size: [1, 1], kind: "hst",    roles: { a: "star_hst", b: "bg_hst" }, orientation: "SE" }, // 5 right
    { pos: [1, 2], size: [1, 1], kind: "hst",    roles: { a: "star_hst", b: "bg_hst" }, orientation: "SW" }, // 6 bottom
    { pos: [0, 1], size: [1, 1], kind: "hst",    roles: { a: "star_hst", b: "bg_hst" }, orientation: "NW" }, // 7 left
    // center square
    { pos: [1, 1], size: [1, 1], kind: "square", roles: { fill: "center" }                                 }, // 8 center
  ],
  // Sew in 3 rows; alternate press direction (rows 0+2 right, row 1 left) so
  // seam allowances nest at intersections. Row-join seams press open.
  seam_graph: [
    { a: 0, b: 4, press: "to_b" }, // row 0: TL-corner ↔ top-HST    (press right)
    { a: 4, b: 1, press: "to_b" }, // row 0: top-HST ↔ TR-corner    (press right)
    { a: 7, b: 8, press: "to_a" }, // row 1: left-HST ↔ center      (press left)
    { a: 8, b: 5, press: "to_a" }, // row 1: center ↔ right-HST     (press left)
    { a: 2, b: 6, press: "to_b" }, // row 2: BL-corner ↔ bottom-HST (press right)
    { a: 6, b: 3, press: "to_b" }, // row 2: bottom-HST ↔ BR-corner (press right)
    { a: 0, b: 7, press: "open" }, // row join col 0
    { a: 4, b: 8, press: "open" }, // row join col 1
    { a: 1, b: 5, press: "open" }, // row join col 2
    { a: 7, b: 2, press: "open" }, // row join col 0
    { a: 8, b: 6, press: "open" }, // row join col 1
    { a: 5, b: 3, press: "open" }, // row join col 2
  ],
}

// Classic Rail Fence: the four-patch version taught in most beginner textbooks.
// Four 2×2 quadrants arranged in a 2×2 — two opposite quadrants (TL + BR) have
// vertical strips; the other two (TR + BL) carry the same strips rotated 90° CW
// via Face.rotation. Two fabrics alternate A-B within each quadrant, producing
// the characteristic diagonal zigzag when multiple blocks are set together.
//
// The existing `rail_fence` schema is a simpler 3-strip × 1-quadrant block
// (3 fabrics, non-square 3×2 grid). This is the square, 2-fabric, 4-quadrant
// version — the canonical book Rail Fence.
const CLASSIC_RAIL_FENCE_SCHEMA: BlockSchema = {
  name: "classic_rail_fence",
  label: "Classic Rail Fence",
  grid_units: [4, 4],
  patches: [
    { name: "strip_a", kind: "strip", size_units: 1, length_units: 2, count: 4, default_role: "background", label: "strip A", assembly_hint: "strip_pieced" },
    { name: "strip_b", kind: "strip", size_units: 1, length_units: 2, count: 4, default_role: "points",     label: "strip B", assembly_hint: "strip_pieced" },
  ],
  faces: [
    // TL quadrant — vertical strips
    { pos: [0, 0], size: [1, 2], kind: "strip", roles: { fill: "strip_a" }, orientation: "V"              }, // 0 TL-A
    { pos: [1, 0], size: [1, 2], kind: "strip", roles: { fill: "strip_b" }, orientation: "V"              }, // 1 TL-B
    // TR quadrant — same strips rotated 90° CW (appear horizontal)
    { pos: [2, 0], size: [2, 1], kind: "strip", roles: { fill: "strip_a" }, orientation: "V", rotation: 1 }, // 2 TR-A
    { pos: [2, 1], size: [2, 1], kind: "strip", roles: { fill: "strip_b" }, orientation: "V", rotation: 1 }, // 3 TR-B
    // BL quadrant — same strips rotated 90° CW (appear horizontal)
    { pos: [0, 2], size: [2, 1], kind: "strip", roles: { fill: "strip_a" }, orientation: "V", rotation: 1 }, // 4 BL-A
    { pos: [0, 3], size: [2, 1], kind: "strip", roles: { fill: "strip_b" }, orientation: "V", rotation: 1 }, // 5 BL-B
    // BR quadrant — vertical strips (same orientation as TL)
    { pos: [2, 2], size: [1, 2], kind: "strip", roles: { fill: "strip_a" }, orientation: "V"              }, // 6 BR-A
    { pos: [3, 2], size: [1, 2], kind: "strip", roles: { fill: "strip_b" }, orientation: "V"              }, // 7 BR-B
  ],
  seam_graph: [
    // Stage 1: A-B strip seam within each quadrant
    { a: 0, b: 1, press: "to_b", stage: 1 }, // TL: A→B, press right
    { a: 2, b: 3, press: "to_b", stage: 1 }, // TR: A→B, press down (rotated quadrant)
    { a: 4, b: 5, press: "to_b", stage: 1 }, // BL: A→B, press down (rotated quadrant)
    { a: 6, b: 7, press: "to_b", stage: 1 }, // BR: A→B, press right
    // Stage 2: join quadrant pairs into rows
    { a: 1, b: 2, press: "open", stage: 2 }, // TL-TR join at x=2
    { a: 5, b: 6, press: "open", stage: 2 }, // BL-BR join at x=2
    // Stage 3: join rows
    { a: 1, b: 4, press: "open", stage: 3 }, // top row to bottom row at y=2
  ],
}

// Bow Tie: a 4×4 four-patch block. Two background squares sit at TL and BR;
// two HSTs occupy TR and BL with the bow-tie (knot) fabric in the inner corners
// (SW of TR, NE of BL) so the two bow triangles point toward the block center.
// The knot triangles touch at the center point; when blocks are placed together
// the knots alternate and the classic bow-tie tiling emerges. Two-fabric palette:
// `bow` (the tie color, fills the two knot triangles) and `background` (fills
// the two corner squares and the two outer HST halves). First four-patch HST
// block — joins Pinwheel (four HSTs) and Classic Rail Fence (four strip
// quadrants) in the four-patch family.
const BOW_TIE_SCHEMA: BlockSchema = {
  name: "bow_tie",
  label: "Bow Tie",
  grid_units: [4, 4],
  patches: [
    { name: "bg_sq",  kind: "square", size_units: 2, count: 2, default_role: "background", label: "background square" },
    { name: "bow_hst", kind: "hst",   size_units: 2, count: 1, default_role: "bow",        label: "bow HST",          assembly_hint: "hst_two_at_a_time" },
    { name: "bg_hst",  kind: "hst",   size_units: 2, count: 1, default_role: "background", label: "background HST",   assembly_hint: "hst_two_at_a_time" },
  ],
  faces: [
    { pos: [0, 0], size: [2, 2], kind: "square", roles: { fill: "bg_sq"  }                                              }, // 0 TL — background corner
    { pos: [2, 0], size: [2, 2], kind: "hst",    roles: { a: "bow_hst", b: "bg_hst" }, orientation: "SW"                }, // 1 TR — bow in SW (inner) corner
    { pos: [0, 2], size: [2, 2], kind: "hst",    roles: { a: "bow_hst", b: "bg_hst" }, orientation: "NE"                }, // 2 BL — bow in NE (inner) corner
    { pos: [2, 2], size: [2, 2], kind: "square", roles: { fill: "bg_sq"  }                                              }, // 3 BR — background corner
  ],
  // Two-row construction: top row (TL + TR), bottom row (BL + BR), then join.
  // Press toward the background squares to keep seam allowances away from the
  // knot and reduce bulk at the center point.
  seam_graph: [
    { a: 0, b: 1, press: "to_a", stage: 1 }, // top row: TL bg-sq ↔ TR bow-HST (press toward square)
    { a: 2, b: 3, press: "to_b", stage: 1 }, // bottom row: BL bow-HST ↔ BR bg-sq (press toward square)
    { a: 0, b: 2, press: "open", stage: 2 }, // join rows at y=2
  ],
}

// Variable Star — 4×4 star block. Same flying-geese ring as the Sawtooth Star,
// but the four plain corner squares are replaced by HST units whose star-colour
// triangle points inward toward the block centre. This creates eight star points
// total (four from geese + four from corner HSTs) with three fabrics: centre,
// points (star colour), and background.
const VARIABLE_STAR_SCHEMA: BlockSchema = {
  name: "variable_star",
  label: "Variable Star",
  grid_units: [4, 4],
  patches: [
    { name: "center",       kind: "square",             size_units: 2, count: 1, default_role: "center",     label: "center" },
    { name: "corner_star",  kind: "hst",                size_units: 1, count: 2, default_role: "points",     label: "corner star HST",       assembly_hint: "hst_two_at_a_time" },
    { name: "corner_bg",    kind: "hst",                size_units: 1, count: 2, default_role: "background", label: "corner background HST", assembly_hint: "hst_two_at_a_time" },
    { name: "goose_large",  kind: "flying_goose_large", size_units: 2, count: 1, default_role: "background", label: "goose large sq",         assembly_hint: "flying_geese_no_waste" },
    { name: "goose_small",  kind: "flying_goose_small", size_units: 1, count: 4, default_role: "points",     label: "goose small sq",         assembly_hint: "flying_geese_no_waste" },
  ],
  faces: [
    { pos: [0, 0], size: [1, 1], kind: "hst",          roles: { a: "corner_star", b: "corner_bg" }, orientation: "SE" }, // 0 TL — star points toward block centre (SE)
    { pos: [3, 0], size: [1, 1], kind: "hst",          roles: { a: "corner_star", b: "corner_bg" }, orientation: "SW" }, // 1 TR — star points toward block centre (SW)
    { pos: [0, 3], size: [1, 1], kind: "hst",          roles: { a: "corner_star", b: "corner_bg" }, orientation: "NE" }, // 2 BL — star points toward block centre (NE)
    { pos: [3, 3], size: [1, 1], kind: "hst",          roles: { a: "corner_star", b: "corner_bg" }, orientation: "NW" }, // 3 BR — star points toward block centre (NW)
    { pos: [1, 1], size: [2, 2], kind: "square",       roles: { fill: "center" }                                      }, // 4 centre
    { pos: [1, 0], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "S" }, // 5 top goose
    { pos: [1, 3], size: [2, 1], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "N" }, // 6 bottom goose
    { pos: [0, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "E" }, // 7 left goose
    { pos: [3, 1], size: [1, 2], kind: "flying_goose", roles: { body: "goose_large", sky: "goose_small" }, orientation: "W" }, // 8 right goose
  ],
  // Three-strip construction (same as Sawtooth Star): top strip (TL-HST +
  // top-goose + TR-HST), centre strip (left-goose + centre + right-goose),
  // bottom strip (BL-HST + bottom-goose + BR-HST). Stage 1 joins corners to
  // geese; stage 2 joins the resulting strips to the centre square.
  seam_graph: [
    { a: 0, b: 5, press: "to_a", stage: 1 }, // TL-HST ↔ top-goose: V seam x=1
    { a: 1, b: 5, press: "to_a", stage: 1 }, // TR-HST ↔ top-goose: V seam x=3
    { a: 0, b: 7, press: "to_a", stage: 1 }, // TL-HST ↔ left-goose: H seam y=1
    { a: 2, b: 7, press: "to_a", stage: 1 }, // BL-HST ↔ left-goose: H seam y=3
    { a: 1, b: 8, press: "to_a", stage: 1 }, // TR-HST ↔ right-goose: H seam y=1
    { a: 3, b: 8, press: "to_a", stage: 1 }, // BR-HST ↔ right-goose: H seam y=3
    { a: 2, b: 6, press: "to_a", stage: 1 }, // BL-HST ↔ bottom-goose: V seam x=1
    { a: 3, b: 6, press: "to_a", stage: 1 }, // BR-HST ↔ bottom-goose: V seam x=3
    { a: 5, b: 4, press: "to_b", stage: 2 }, // top-goose ↔ centre: H seam y=1
    { a: 7, b: 4, press: "to_b", stage: 2 }, // left-goose ↔ centre: V seam x=1
    { a: 8, b: 4, press: "to_b", stage: 2 }, // right-goose ↔ centre: V seam x=3
    { a: 6, b: 4, press: "to_b", stage: 2 }, // bottom-goose ↔ centre: H seam y=3
  ],
}

const SCHEMAS: Record<string, BlockSchema> = {
  sawtooth_star: SAWTOOTH_STAR_SCHEMA,
  nine_patch: NINE_PATCH_SCHEMA,
  pinwheel: PINWHEEL_SCHEMA,
  ohio_star: OHIO_STAR_SCHEMA,
  rail_fence: RAIL_FENCE_SCHEMA,
  plain_block: PLAIN_BLOCK_SCHEMA,
  five_patch: FIVE_PATCH_SCHEMA,
  log_cabin: LOG_CABIN_SCHEMA,
  courthouse_steps: COURTHOUSE_STEPS_SCHEMA,
  churn_dash: CHURN_DASH_SCHEMA,
  shoofly: SHOOFLY_SCHEMA,
  friendship_star: FRIENDSHIP_STAR_SCHEMA,
  classic_rail_fence: CLASSIC_RAIL_FENCE_SCHEMA,
  bow_tie: BOW_TIE_SCHEMA,
  variable_star: VARIABLE_STAR_SCHEMA,
}

// Falls back to the default schema for unknown styles. The block_set tool
// validates `style` against `registeredSchemaNames()` so live calls cannot
// land here; the fallback only protects against stale persisted state.
export function getSchema(style: string): BlockSchema {
  return SCHEMAS[style] ?? SCHEMAS[DEFAULT_BLOCK_STYLE]!
}

export function schemaLabel(style: string): string {
  return (SCHEMAS[style] ?? SCHEMAS[DEFAULT_BLOCK_STYLE]!).label
}

// Ordered, deduplicated list of assembly hints a schema's patches reference.
// Hint order follows first-appearance in `schema.patches`, which keeps
// renderer output stable and matches the order patches were declared in.
export function getAssemblyHints(schema: BlockSchema): AssemblyHint[] {
  const seen = new Set<AssemblyHint>()
  const out: AssemblyHint[] = []
  for (const p of schema.patches) {
    if (p.assembly_hint && !seen.has(p.assembly_hint)) {
      seen.add(p.assembly_hint)
      out.push(p.assembly_hint)
    }
  }
  return out
}

// Schema-driven "Pieces / block" summary for the PDF overview, e.g.
// "1 center, 4 corners, 4 flying geese". Reports finished-piece counts, not
// cut-square counts: the 1-large-+-4-small goose pair collapses to one
// "4 flying geese" entry (sky count = number of geese), HST patch pairs
// collapse to one "N HSTs" entry, and QST patch pairs collapse to one
// "N QSTs" entry (each face is one finished QST regardless of how many cut
// squares feed it). Strip patches collapse to one "N strips" entry.
export function piecesPerBlockSummary(schema: BlockSchema): string {
  const parts: string[] = []
  let goose: { count: number } | null = null
  for (const p of schema.patches) {
    switch (p.kind) {
      case "flying_goose_small": goose = { count: p.count }; break
      case "flying_goose_large": break  // counted via sky (goose_small) count
      case "hst":                break  // collapsed to face count below
      case "qst":                break  // collapsed to face count below
      case "strip":              break  // collapsed to face count below
      case "square": parts.push(`${p.count} ${p.label}${p.count === 1 ? "" : "s"}`); break
      default: assertNever(p.kind)
    }
  }
  if (goose) parts.push(`${goose.count} flying ${goose.count === 1 ? "goose" : "geese"}`)
  const hstFaces = schema.faces.filter((f) => f.kind === "hst").length
  if (hstFaces > 0) parts.push(`${hstFaces} HST${hstFaces === 1 ? "" : "s"}`)
  const qstFaces = schema.faces.filter((f) => f.kind === "qst").length
  if (qstFaces > 0) parts.push(`${qstFaces} QST${qstFaces === 1 ? "" : "s"}`)
  const stripFaces = schema.faces.filter((f) => f.kind === "strip").length
  if (stripFaces > 0) parts.push(`${stripFaces} strip${stripFaces === 1 ? "" : "s"}`)
  return parts.join(", ")
}

export function registeredSchemaNames(): string[] {
  return Object.keys(SCHEMAS)
}

// True for faces whose cut edges run on the bias (diagonal grain).
export function isBiasFace(face: Face): boolean {
  switch (face.kind) {
    case "hst":
    case "qst":
    case "flying_goose": return true
    case "square":
    case "strip":        return false
    default:             return assertNever(face.kind)
  }
}

// Compose face.orientation with face.rotation (quarter-turns clockwise) to get
// the effective rendered orientation. Used by all rendering paths so Face.rotation
// is never inspected directly outside this function.
//
// strip:        odd rotation toggles H ↔ V (vertical strip rotated once appears horizontal).
// flying_goose: cycles N→E→S→W (cardinal ring, each step is 90° CW).
// hst:          cycles NE→SE→SW→NW (diagonal ring, each step is 90° CW).
// qst:          90°-symmetric in both "+" and "x" forms; rotation changes which pair of
//               triangles is a/b but not the gross shape — returns face.orientation unchanged.
// square:       rotationally symmetric — returns face.orientation unchanged.
export function effectiveOrientation(face: Face): FaceOrientation | undefined {
  const r = ((face.rotation ?? 0) % 4 + 4) % 4
  if (r === 0) return face.orientation
  switch (face.kind) {
    case "square":
      return face.orientation
    case "strip": {
      const base = face.orientation ?? "V"
      return r % 2 === 1 ? (base === "V" ? "H" : "V") : base
    }
    case "flying_goose": {
      const order = ["N", "E", "S", "W"] as const
      const base = (face.orientation ?? "S") as "N" | "E" | "S" | "W"
      const idx = order.indexOf(base)
      return idx < 0 ? face.orientation : order[(idx + r) % 4]
    }
    case "hst": {
      const order = ["NE", "SE", "SW", "NW"] as const
      const base = (face.orientation ?? "NE") as "NE" | "SE" | "SW" | "NW"
      const idx = order.indexOf(base)
      return idx < 0 ? face.orientation : order[(idx + r) % 4]
    }
    case "qst":
      return face.orientation
    default:
      return assertNever(face.kind)
  }
}

// Number of faces with bias-cut edges. Higher values mean more bias seams —
// handle these blocks with starch before cutting.
export function biasEdgeCount(schema: BlockSchema): number {
  return schema.faces.filter(isBiasFace).length
}

// Quilting taxonomy: which patch-family does this block belong to?
// Matches the beginner-textbook classification: a four-patch block can be
// quartered with no piece spanning the dividing cuts; nine-patch thirds it;
// five-patch fifths it. Note: Sawtooth Star and Ohio Star are NOT four-patch —
// their 2×2 center spans the midline.
export type BlockFamily = "four-patch" | "nine-patch" | "five-patch" | "strip" | "log-cabin" | "other"

export function patternFamily(schema: BlockSchema): BlockFamily {
  if (schema.patches.some(p => p.assembly_hint === "log_cabin_spiral" || p.assembly_hint === "courthouse_steps"))
    return "log-cabin"
  const [cols, rows] = schema.grid_units
  if (cols !== rows) return "strip"
  const crossesLine = (axis: 0 | 1, at: number) =>
    schema.faces.some(f => f.pos[axis] < at && f.pos[axis] + f.size[axis] > at)
  const anyLineCrossed = (n: number): boolean => {
    for (let i = 1; i < n; i++)
      if (crossesLine(0, (cols / n) * i) || crossesLine(1, (rows / n) * i)) return true
    return false
  }
  if (cols % 2 === 0 && !anyLineCrossed(2)) return "four-patch"
  if (cols % 3 === 0 && !anyLineCrossed(3)) return "nine-patch"
  if (cols % 5 === 0 && !anyLineCrossed(5)) return "five-patch"
  return "other"
}

// Cut size in inches for one patch given the active block's unit size and seam.
// For square / triangle patches this is the side of the cut square. For strip
// patches, this is the SHORT edge (the strip width) — pair with `cutLength`
// for the long edge.
// The seam-allowance scaling for the no-waste flying-geese formulas is centralised
// here so each Patch declaration stays to four numbers.
export function cutSize(patch: Patch, block: Block): number {
  const u = block.unit_inches
  const s = block.seam
  const seamScale = s / 0.25
  switch (patch.kind) {
    case "square":
      return patch.size_units * u + 2 * s
    case "flying_goose_large":
      return patch.size_units * u + 1.25 * seamScale
    case "flying_goose_small":
      return patch.size_units * u + 0.875 * seamScale
    case "hst":
      // Two-at-a-time HST: cut a square `finished + 7/8"`, sew on the
      // diagonal, cross-cut, get two HSTs of the finished edge size.
      return patch.size_units * u + 0.875 * seamScale
    case "qst":
      // Four-at-a-time QST: cut a square `finished + 1 1/4"`, layer with a
      // partner square, sew on both diagonals, cross-cut → 4 QSTs of the
      // finished edge size.
      return patch.size_units * u + 1.25 * seamScale
    case "strip":
      // Short edge (strip width). Long edge comes from `cutLength`.
      return patch.size_units * u + 2 * s
    default:
      return assertNever(patch.kind)
  }
}

// Long-edge cut length in inches. Equal to `cutSize` for square / triangle
// patches; for strip patches uses `length_units` (defaulting to `size_units`
// when missing, which collapses a strip back to a square — primarily for
// future extensibility, not a code path Rail Fence exercises). Strip-pieced
// blocks use this for the cross-cut length once strips are sewn lengthwise.
export function cutLength(patch: Patch, block: Block): number {
  if (patch.kind === "strip") {
    const lu = patch.length_units ?? patch.size_units
    return lu * block.unit_inches + 2 * block.seam
  }
  return cutSize(patch, block)
}

export interface SchemaPiece {
  patchName: string // patch name (center, corner, goose_large, goose_small, ...)
  size: number      // short-edge cut size in inches (includes seam allowance)
  length: number    // long-edge cut size in inches; equal to `size` for squares
  count: number     // per-block count
}

export function schemaPieces(block: Block): SchemaPiece[] {
  const schema = getSchema(block.style)
  return schema.patches.map((p) => ({
    patchName: p.name,
    size: cutSize(p, block),
    length: cutLength(p, block),
    count: p.count,
  }))
}

export function defaultPieceRolesFor(schema: BlockSchema): Record<string, string> {
  const out: Record<string, string> = {}
  for (const p of schema.patches) out[p.name] = p.default_role
  return out
}

// Finished block dimensions in inches, (width, height). Driven by the schema's
// grid_units so non-4×4 blocks (Rail Fence, Nine-Patch with a 3×3 grid, …)
// carry the right size everywhere downstream.
export function blockFinishedInches(block: Block): [number, number] {
  const [cols, rows] = getSchema(block.style).grid_units
  return [cols * block.unit_inches, rows * block.unit_inches]
}

// Resolve a face role-key (e.g. "fill", "body", "sky") to the fabric color in
// `colors`. Walks: face.roles[key] → patch.name → block.piece_roles override
// or patch.default_role → colors[fabric role].
export function faceFillColor(
  face: Face,
  roleKey: string,
  schema: BlockSchema,
  block: Block,
  colors: Record<string, string>,
): string {
  const patchName = face.roles[roleKey]
  const patch = schema.patches.find((p) => p.name === patchName)
  if (!patch) return colors.background ?? "#888"
  const fabricRole = block.piece_roles[patchName] ?? patch.default_role
  return colors[fabricRole] ?? colors[patch.default_role] ?? "#888"
}

// Look up a patch's human label from a schema by patch name.
export function patchLabel(schema: BlockSchema, patchName: string): string {
  return schema.patches.find((p) => p.name === patchName)?.label ?? patchName
}

// Unique block slots consumed by a schema, derived from patch.default_role.
// Pinwheel → ["points", "background"]; all star schemas → ["center", "background", "points"].
// "Slot" is the block-level term for a fabric assignment key (the per-schema
// counterpart to structural roles like binding/sashing).
export function schemaSlots(schema: BlockSchema): string[] {
  return [...new Set(schema.patches.map((p) => p.default_role))]
}

// Union of every slot key contributed by any registered schema. Used to
// validate fabric.slot at tool boundaries without hard-coding a list.
export function allBlockSlots(): string[] {
  const seen = new Set<string>()
  for (const s of Object.values(SCHEMAS)) {
    for (const p of s.patches) seen.add(p.default_role)
  }
  return [...seen]
}

// Role keys that address the sub-regions of a face. Square and strip faces
// have a single "fill" region; HST and QST split into "a"/"b" triangles;
// flying_goose splits into "body" (apex triangle) and "sky" (two corner
// triangles). Used by renderers and label collectors so the branching lives
// in one place rather than scattered across block_svg, pdf, and tools.
export function faceRoleKeys(face: Face): string[] {
  switch (face.kind) {
    case "square": case "strip":    return ["fill"]
    case "hst":    case "qst":      return ["a", "b"]
    case "flying_goose":            return ["body", "sky"]
    default:                        return assertNever(face.kind)
  }
}

// Short text label for a face as it appears in the block-grid ASCII diagram
// and the PDF assembly page. Encodes face kind + orientation into a compact
// string so both rendering paths share identical output without duplicating
// the branching logic.
const CARDINAL_ARROW: Record<string, string> = { N: "^", S: "v", E: ">", W: "<" }

export function faceLabelForGrid(face: Face, schema: BlockSchema): string {
  switch (face.kind) {
    case "square":
      return schema.patches.find((p) => p.name === face.roles.fill)?.label ?? face.roles.fill
    case "hst":
      return `HST ${effectiveOrientation(face) ?? "NE"}`
    case "qst":
      return `QST ${effectiveOrientation(face) ?? "+"}`
    case "strip": {
      const label = schema.patches.find((p) => p.name === face.roles.fill)?.label ?? face.roles.fill
      return `${label} ${effectiveOrientation(face) ?? "H"}`
    }
    case "flying_goose":
      return `goose ${CARDINAL_ARROW[effectiveOrientation(face) ?? "S"] ?? "?"}`
    default:
      return assertNever(face.kind)
  }
}
